import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck,
  Shield,
  Check,
  X,
  Info,
  ChevronDown,
  ChevronRight,
  Save,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Role {
  id: string;
  role_key: string;
  role_name: string;
  description?: string;
  scope: string;
  is_bootstrap: boolean;
  is_active: boolean;
  permissions: string[]; // array of permission_key strings
}

interface ScreenGroup {
  id: string;
  label: string;
  icon: string;
  screens: ScreenItem[];
}

interface ScreenItem {
  id: string;
  label: string;
  path?: string;
  /** All permission keys — a role needs at least one of these to access the screen */
  requiredPermissions: string[];
  description?: string;
}

// ─── Screen registry derived from navigation.config ──────────────────────────
// We hard-code the representative required-permission per screen rather than
// importing the full NavigationConfig (which pulls in Lucide dynamically).
// The key permission is the first non-wildcard entry from requiredPermission.

const SCREEN_GROUPS: ScreenGroup[] = [
  {
    id: 'command-center',
    label: 'Command Center',
    icon: '🏠',
    screens: [
      { id: 'dashboard',    label: 'Dashboard',       path: '/dashboard',  requiredPermissions: ['analytics:view'] },
      { id: 'missions',     label: 'Mission Control', path: '/missions',   requiredPermissions: ['missions:view'] },
      { id: 'ai-analysis',  label: 'AI Analysis',     path: '/analysis',   requiredPermissions: ['analytics:view', 'ai:query'] },
    ],
  },
  {
    id: 'agrotech',
    label: 'AgroTech Estate Intelligence',
    icon: '🌱',
    screens: [
      { id: 'agrotech-dashboard',     label: 'Operations Dashboard',    path: '/agrotech/dashboard',       requiredPermissions: ['analytics:view'] },
      { id: 'agrotech-boundaries',    label: 'Field Boundaries',        path: '/agrotech/boundaries',      requiredPermissions: ['missions:view'] },
      { id: 'agrotech-temporal',      label: 'Temporal Analysis',       path: '/agrotech/temporal',        requiredPermissions: ['analytics:view'] },
      { id: 'agrotech-anomalies',     label: 'Anomaly Alerts',          path: '/agrotech/anomalies',       requiredPermissions: ['missions:view'] },
      { id: 'agrotech-survey',        label: 'Survey Mission Planner',  path: '/agrotech/survey-planner',  requiredPermissions: ['missions:view'] },
      { id: 'agrotech-flight',        label: 'Flight Data & Analysis',  path: '/agrotech/flight-analysis', requiredPermissions: ['missions:write'] },
      { id: 'agrotech-review',        label: 'Analysis Review',         path: '/agrotech/analysis-review', requiredPermissions: ['missions:write'] },
      { id: 'agrotech-remediation',   label: 'Remediation Missions',    path: '/agrotech/remediation',     requiredPermissions: ['missions:write'] },
      { id: 'agrotech-efficacy',      label: 'Treatment Efficacy',      path: '/agrotech/efficacy',        requiredPermissions: ['analytics:view'] },
      { id: 'agrotech-reports',       label: 'Crop Health Reports',     path: '/agrotech/reports',         requiredPermissions: ['analytics:view'] },
      { id: 'agrotech-ask-nabhos',    label: 'Ask Nabhos (Step 9 NLQ)', path: '/agrotech/ask-nabhos',      requiredPermissions: ['analytics:view'] },
    ],
  },
  {
    id: 'precision',
    label: 'Precision Planning',
    icon: '🎯',
    screens: [
      { id: 'precision-variable-rate', label: 'Variable Rate Scripting', path: '/precision/variable-rate',    requiredPermissions: ['missions:write'] },
      { id: 'precision-yield',         label: 'Yield Forecasting',        path: '/precision/yield',            requiredPermissions: ['analytics:view'] },
      { id: 'precision-suitability',   label: 'Crop Suitability',         path: '/precision/crop-suitability', requiredPermissions: ['analytics:view'] },
      { id: 'precision-land',          label: 'Land Expansion',           path: '/precision/land-expansion',   requiredPermissions: ['analytics:view'] },
      { id: 'precision-compliance',    label: 'Chemical Compliance',      path: '/precision/compliance',       requiredPermissions: ['missions:view'] },
    ],
  },
  {
    id: 'infrastructure',
    label: 'Infrastructure Intelligence',
    icon: '🏗️',
    screens: [
      { id: 'infra-dashboard',    label: 'Operations Dashboard',   path: '/infrastructure/dashboard',       requiredPermissions: ['analytics:view'] },
      { id: 'infra-boundaries',   label: 'Asset Boundaries',       path: '/infrastructure/boundaries',      requiredPermissions: ['missions:view'] },
      { id: 'infra-temporal',     label: 'Temporal Analysis',      path: '/infrastructure/temporal',        requiredPermissions: ['analytics:view'] },
      { id: 'infra-anomalies',    label: 'Anomaly Alerts',         path: '/infrastructure/anomalies',       requiredPermissions: ['missions:view'] },
      { id: 'infra-survey',       label: 'Survey Mission Planner', path: '/infrastructure/survey-planner',  requiredPermissions: ['missions:view'] },
      { id: 'infra-flight',       label: 'Flight Data & Analysis', path: '/infrastructure/flight-analysis', requiredPermissions: ['missions:write'] },
      { id: 'infra-review',       label: 'Analysis Review',        path: '/infrastructure/analysis-review', requiredPermissions: ['missions:write'] },
      { id: 'infra-response',     label: 'Response Planning',      path: '/infrastructure/response',        requiredPermissions: ['missions:write'] },
      { id: 'infra-construction', label: 'Construction Progress',  path: '/infrastructure/construction',    requiredPermissions: ['analytics:view'] },
      { id: 'infra-encroachment', label: 'Encroachment Detection', path: '/infrastructure/encroachment',    requiredPermissions: ['analytics:view'] },
    ],
  },
  {
    id: 'oil-gas',
    label: 'Oil & Gas Intelligence',
    icon: '⚙️',
    screens: [
      { id: 'og-dashboard',   label: 'O&G Dashboard',       path: '/oil-gas/dashboard',       requiredPermissions: ['analytics:view'] },
      { id: 'og-boundaries',  label: 'Corridor Boundaries', path: '/oil-gas/boundaries',      requiredPermissions: ['missions:view'] },
      { id: 'og-temporal',    label: 'Temporal Analysis',   path: '/oil-gas/temporal',        requiredPermissions: ['analytics:view'] },
      { id: 'og-anomalies',   label: 'Anomaly Alerts',      path: '/oil-gas/anomalies',       requiredPermissions: ['missions:view'] },
      { id: 'og-survey',      label: 'Survey Planner',      path: '/oil-gas/survey-planner',  requiredPermissions: ['missions:view'] },
      { id: 'og-flight',      label: 'Flight Analysis',     path: '/oil-gas/flight-analysis', requiredPermissions: ['missions:write'] },
      { id: 'og-review',      label: 'Analysis Review',     path: '/oil-gas/analysis-review', requiredPermissions: ['missions:write'] },
      { id: 'og-response',    label: 'Response Planning',   path: '/oil-gas/response',        requiredPermissions: ['missions:write'] },
      { id: 'og-plume',       label: 'Plume / Leak Analyser', path: '/oil-gas/plume-analyzer', requiredPermissions: ['analytics:view'] },
      { id: 'og-exploration', label: 'Upstream Exploration',  path: '/oil-gas/exploration',   requiredPermissions: ['analytics:view'] },
      { id: 'og-facility',    label: 'Facility Safety',     path: '/oil-gas/facility-safety', requiredPermissions: ['missions:view'] },
      { id: 'og-regulatory',  label: 'Regulatory Reporting', path: '/oil-gas/regulatory',    requiredPermissions: ['analytics:view'] },
    ],
  },
  {
    id: 'environmental',
    label: 'Environmental Geotech',
    icon: '🌍',
    screens: [
      { id: 'env-dashboard',    label: 'Operations Dashboard',   path: '/environmental/dashboard',        requiredPermissions: ['analytics:view'] },
      { id: 'env-boundaries',   label: 'Zone Boundaries',        path: '/environmental/boundaries',       requiredPermissions: ['missions:view'] },
      { id: 'env-temporal',     label: 'Temporal Analysis',       path: '/environmental/temporal',        requiredPermissions: ['analytics:view'] },
      { id: 'env-anomalies',    label: 'Anomaly Alerts',          path: '/environmental/anomalies',       requiredPermissions: ['missions:view'] },
      { id: 'env-survey',       label: 'Survey Planner',          path: '/environmental/survey-planner',  requiredPermissions: ['missions:view'] },
      { id: 'env-flight',       label: 'Flight Analysis',         path: '/environmental/flight-analysis', requiredPermissions: ['missions:write'] },
      { id: 'env-review',       label: 'Analysis Review',         path: '/environmental/analysis-review', requiredPermissions: ['missions:write'] },
      { id: 'env-enforcement',  label: 'Enforcement Actions',     path: '/environmental/enforcement',     requiredPermissions: ['missions:view'] },
      { id: 'env-carbon',       label: 'Carbon Credit Verification', path: '/environmental/carbon',       requiredPermissions: ['analytics:view'] },
      { id: 'env-geohazard',    label: 'Geohazard Risk',          path: '/environmental/geohazard',       requiredPermissions: ['analytics:view'] },
    ],
  },
  {
    id: 'linear-assets',
    label: 'Linear Assets (Power & Rail)',
    icon: '⚡',
    screens: [
      { id: 'lin-dashboard',   label: 'Operations Dashboard',      path: '/linear-assets/dashboard',      requiredPermissions: ['analytics:view'] },
      { id: 'lin-corridors',   label: 'Corridor Boundaries',       path: '/linear-assets/corridors',      requiredPermissions: ['missions:view'] },
      { id: 'lin-temporal',    label: 'Temporal Analysis',         path: '/linear-assets/temporal',       requiredPermissions: ['analytics:view'] },
      { id: 'lin-encroach',    label: 'Encroachment Projections',  path: '/linear-assets/encroachment',   requiredPermissions: ['missions:view'] },
      { id: 'lin-obstruct',    label: 'Track Obstructions',        path: '/linear-assets/obstructions',   requiredPermissions: ['missions:view'] },
      { id: 'lin-survey',      label: 'Survey Planner',            path: '/linear-assets/survey-planner', requiredPermissions: ['missions:view'] },
      { id: 'lin-flight',      label: 'Flight Data & Diagnostics', path: '/linear-assets/flight-analysis', requiredPermissions: ['missions:write'] },
      { id: 'lin-review',      label: 'Analysis Review',           path: '/linear-assets/analysis-review', requiredPermissions: ['missions:write'] },
      { id: 'lin-response',    label: 'Clearance Planning',        path: '/linear-assets/response',       requiredPermissions: ['missions:write'] },
      { id: 'lin-work-orders', label: 'Work Orders & Audit',       path: '/linear-assets/work-orders',    requiredPermissions: ['missions:view'] },
      { id: 'lin-reports',     label: 'Compliance Reports',        path: '/linear-assets/reports',        requiredPermissions: ['analytics:view'] },
      { id: 'lin-efficacy',    label: 'Clearance Efficacy',        path: '/linear-assets/efficacy',       requiredPermissions: ['analytics:view'] },
    ],
  },
  {
    id: 'org-settings',
    label: 'Organisation Settings',
    icon: '⚙️',
    screens: [
      { id: 'user-access',    label: 'User Access Control',     path: '/org-admin/users',              requiredPermissions: ['organization:admin', 'users:write'] },
      { id: 'hierarchy',      label: 'Hierarchy Manager',       path: '/enterprise/hierarchy',         requiredPermissions: ['hierarchy:manage'] },
      { id: 'assets',         label: 'Approved Assets',         path: '/settings/approved-assets',     requiredPermissions: ['organization:admin', 'assets:manage'] },
      { id: 'uom',            label: 'Units & ROI Parameters',  path: '/settings/units-of-measure',    requiredPermissions: ['organization:admin', 'settings:write'] },
      { id: 'ai-config',      label: 'AI Configuration',        path: '/settings/ai-configuration',    requiredPermissions: ['organization:admin'] },
      { id: 'webhooks',       label: 'Webhooks',                path: '/enterprise/webhooks',          requiredPermissions: ['webhooks:manage'] },
      { id: 'notifications',  label: 'Notifications',           path: '/enterprise/notifications',     requiredPermissions: ['notifications:manage'] },
      { id: 'audit',          label: 'Audit Trail',             path: '/enterprise/audit',             requiredPermissions: ['audit:view'] },
      { id: 'integrity',      label: 'Data Integrity',          path: '/enterprise/integrity',         requiredPermissions: ['integrity:view'] },
      { id: 'mfa',            label: 'Security & MFA',          path: '/enterprise/mfa',               requiredPermissions: ['organization:admin', 'security:manage'] },
    ],
  },
];

// ─── Helper ───────────────────────────────────────────────────────────────────

/** Returns true if the role's permissions grant access to the screen. */
function roleCanAccess(rolePerms: string[], screen: ScreenItem): boolean {
  if (rolePerms.includes('*.*')) return true;
  return screen.requiredPermissions.some((p) => rolePerms.includes(p));
}

/** Returns the first non-wildcard required permission for a screen (used when granting). */
function grantPermission(screen: ScreenItem): string {
  return screen.requiredPermissions[0];
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function RoleScreenAccessPage() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null); // roleId being saved
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Local overrides: map of roleId → set of permission_keys
  const [pendingPerms, setPendingPerms] = useState<Record<string, Set<string>>>({});
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(SCREEN_GROUPS.map((g) => g.id))
  );
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const res = await api.get('/roles');
      const fetched: Role[] = res.data.data;
      setRoles(fetched);
      // Seed pendingPerms from server data
      const seed: Record<string, Set<string>> = {};
      for (const r of fetched) {
        seed[r.id] = new Set(r.permissions);
      }
      setPendingPerms(seed);
      if (fetched.length > 0) setSelectedRole(fetched[0].id);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const toggleAccess = useCallback(
    (roleId: string, screen: ScreenItem) => {
      const role = roles.find((r) => r.id === roleId);
      if (!role || role.is_bootstrap) return;

      setPendingPerms((prev) => {
        const current = new Set(prev[roleId] ?? []);
        const perm = grantPermission(screen);

        if (roleCanAccess(Array.from(current), screen)) {
          // Revoke: remove all required permissions for this screen
          screen.requiredPermissions.forEach((p) => current.delete(p));
        } else {
          // Grant: add the first required permission
          current.add(perm);
        }
        return { ...prev, [roleId]: current };
      });
    },
    [roles]
  );

  const saveRole = async (roleId: string) => {
    const role = roles.find((r) => r.id === roleId);
    if (!role) return;

    setSaving(roleId);
    try {
      const perms = Array.from(pendingPerms[roleId] ?? []);
      // The PUT /roles/:id endpoint accepts permission_ids — but here we have permission_keys.
      // We need to resolve keys → ids. Fetch permissions list and map.
      const permsRes = await api.get('/roles/permissions');
      const allPerms: { id: string; permission_key: string }[] = permsRes.data.data;
      const permIds = perms
        .map((key) => allPerms.find((p) => p.permission_key === key)?.id)
        .filter(Boolean) as string[];

      await api.put(`/roles/${roleId}`, {
        role_name: role.role_name,
        description: role.description,
        permission_ids: permIds,
      });

      setSuccess(`Role "${role.role_name}" updated successfully`);
      await fetchRoles();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to save role');
    } finally {
      setSaving(null);
    }
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const hasPendingChanges = (roleId: string): boolean => {
    const original = roles.find((r) => r.id === roleId);
    if (!original) return false;
    const origSet = new Set(original.permissions);
    const pendSet = pendingPerms[roleId] ?? new Set<string>();
    if (origSet.size !== pendSet.size) return true;
    for (const p of origSet) if (!pendSet.has(p)) return true;
    return false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const activeRole = roles.find((r) => r.id === selectedRole);
  const activePerms = Array.from(pendingPerms[selectedRole ?? ''] ?? []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Role Screen Access
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Control which application screens each role can access. Toggle access per screen,
          then save changes per role.
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert type="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <div className="flex gap-6">
        {/* Left: Role list */}
        <div className="w-64 flex-shrink-0 space-y-2">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            Roles
          </h2>
          {roles.map((role) => (
            <button
              key={role.id}
              onClick={() => setSelectedRole(role.id)}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                selectedRole === role.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  {role.permissions.includes('*.*') ? (
                    <ShieldCheck className="w-4 h-4 flex-shrink-0 text-purple-500" />
                  ) : (
                    <Shield className="w-4 h-4 flex-shrink-0 text-gray-400" />
                  )}
                  <span className="text-sm font-medium truncate">{role.role_name}</span>
                </div>
                {hasPendingChanges(role.id) && (
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-amber-400" title="Unsaved changes" />
                )}
              </div>
              {role.is_bootstrap && (
                <p className="text-xs text-gray-400 mt-1 ml-6">System role (read-only)</p>
              )}
            </button>
          ))}
        </div>

        {/* Right: Screen access matrix for selected role */}
        <div className="flex-1 min-w-0">
          {activeRole ? (
            <Card>
              {/* Role header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-2">
                    {activeRole.permissions.includes('*.*') ? (
                      <ShieldCheck className="w-5 h-5 text-purple-500" />
                    ) : (
                      <Shield className="w-5 h-5 text-gray-400" />
                    )}
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {activeRole.role_name}
                    </h2>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      activeRole.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {activeRole.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {activeRole.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-7">
                      {activeRole.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {activeRole.permissions.includes('*.*') && (
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-sm text-purple-700 dark:text-purple-300">
                      <Info className="w-4 h-4" />
                      Wildcard — full access to all screens
                    </div>
                  )}
                  {!activeRole.is_bootstrap && hasPendingChanges(activeRole.id) && (
                    <Button
                      size="sm"
                      onClick={() => saveRole(activeRole.id)}
                      disabled={saving === activeRole.id}
                      className="flex items-center gap-2"
                    >
                      {saving === activeRole.id ? (
                        <Spinner size="sm" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save Changes
                    </Button>
                  )}
                </div>
              </div>

              {/* Screen groups */}
              <div className="space-y-3">
                {SCREEN_GROUPS.map((group) => {
                  const expanded = expandedGroups.has(group.id);
                  const accessCount = group.screens.filter((s) =>
                    roleCanAccess(activePerms, s)
                  ).length;

                  return (
                    <div
                      key={group.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                    >
                      {/* Group header */}
                      <button
                        onClick={() => toggleGroup(group.id)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-base">{group.icon}</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {group.label}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {accessCount}/{group.screens.length} screens accessible
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Bulk grant/revoke for non-bootstrap roles */}
                          {!activeRole.is_bootstrap && !activeRole.permissions.includes('*.*') && (
                            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => {
                                  setPendingPerms((prev) => {
                                    const current = new Set(prev[activeRole.id] ?? []);
                                    group.screens.forEach((s) => current.add(grantPermission(s)));
                                    return { ...prev, [activeRole.id]: current };
                                  });
                                }}
                                className="px-2 py-1 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                              >
                                Grant all
                              </button>
                              <button
                                onClick={() => {
                                  setPendingPerms((prev) => {
                                    const current = new Set(prev[activeRole.id] ?? []);
                                    group.screens.forEach((s) =>
                                      s.requiredPermissions.forEach((p) => current.delete(p))
                                    );
                                    return { ...prev, [activeRole.id]: current };
                                  });
                                }}
                                className="px-2 py-1 text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                              >
                                Revoke all
                              </button>
                            </div>
                          )}
                          {expanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </button>

                      {/* Screens list */}
                      {expanded && (
                        <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                          {group.screens.map((screen) => {
                            const hasAccess = roleCanAccess(activePerms, screen);
                            const isWildcard = activePerms.includes('*.*');
                            const isBootstrap = activeRole.is_bootstrap;

                            return (
                              <div
                                key={screen.id}
                                className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/30"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                      {screen.label}
                                    </span>
                                    {screen.path && (
                                      <code className="text-xs text-gray-400 dark:text-gray-500">
                                        {screen.path}
                                      </code>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                    Required:{' '}
                                    {screen.requiredPermissions.map((p) => (
                                      <code
                                        key={p}
                                        className="mr-1 px-1 bg-gray-100 dark:bg-gray-700 rounded"
                                      >
                                        {p}
                                      </code>
                                    ))}
                                  </p>
                                </div>

                                {/* Toggle */}
                                <button
                                  onClick={() => toggleAccess(activeRole.id, screen)}
                                  disabled={isBootstrap || isWildcard}
                                  title={
                                    isBootstrap
                                      ? 'System roles cannot be modified'
                                      : isWildcard
                                      ? 'Wildcard role has unrestricted access'
                                      : hasAccess
                                      ? 'Click to revoke access'
                                      : 'Click to grant access'
                                  }
                                  className={`ml-4 flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                                    isBootstrap || isWildcard
                                      ? 'cursor-not-allowed opacity-60'
                                      : 'cursor-pointer'
                                  } ${
                                    hasAccess
                                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                                      : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'
                                  }`}
                                >
                                  {hasAccess ? (
                                    <Check className="w-4 h-4" />
                                  ) : (
                                    <X className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Save footer */}
              {!activeRole.is_bootstrap && hasPendingChanges(activeRole.id) && (
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    You have unsaved changes for this role.
                  </p>
                  <Button
                    onClick={() => saveRole(activeRole.id)}
                    disabled={saving === activeRole.id}
                    className="flex items-center gap-2"
                  >
                    {saving === activeRole.id ? (
                      <Spinner size="sm" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Save Changes
                  </Button>
                </div>
              )}
            </Card>
          ) : (
            <Card>
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                Select a role to manage its screen access.
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// Made with Bob
